import {
  type FormSettings,
  type FormSettingsDocument,
  type FormSubmission,
  type UpdateFormSettingsInput,
  formSettingsSchema,
  formSubmissionSchema,
  updateFormSettingsInputSchema,
} from '../domains/form-settings/schema';
import { apiRequest } from './api-client';

type FormSettingsRow = {
  site_id: string;
  settings: unknown;
  updated_at?: string | null;
};

type SubmitPublicSiteFormInput = {
  siteId: string;
  pagePath: string;
  payload: Record<string, string>;
  honeypotValue?: string;
};

const defaultFormSettings: FormSettingsDocument = {
  enabled: true,
  destinationEmail: '',
  successMessage: 'お問い合わせありがとうございます。内容を確認のうえ、折り返しご連絡します。',
  privacyPolicyUrl: '',
  rateLimitSeconds: 30,
  retentionDays: 90,
  fields: [
    {
      id: 'name',
      label: 'お名前',
      type: 'text',
      required: true,
      placeholder: '例）山田 太郎',
    },
    {
      id: 'email',
      label: 'メールアドレス',
      type: 'email',
      required: true,
      placeholder: 'example@company.jp',
    },
    {
      id: 'message',
      label: 'お問い合わせ内容',
      type: 'textarea',
      required: true,
      placeholder: 'ご相談内容を入力してください',
    },
  ],
};

function buildDefaultFormSettings(siteId: string): FormSettings {
  return formSettingsSchema.parse({
    site_id: siteId,
    settings: defaultFormSettings,
  });
}

function parseFormSettingsRow(row: FormSettingsRow | null | undefined, siteId: string): FormSettings {
  if (!row) {
    return buildDefaultFormSettings(siteId);
  }

  return formSettingsSchema.parse({
    site_id: row.site_id ?? siteId,
    settings: row.settings ?? defaultFormSettings,
    updated_at: row.updated_at ?? undefined,
  });
}

export async function getFormSettings(siteId: string): Promise<FormSettings> {
  const data = await apiRequest<FormSettingsRow | null>(
    `/api/sites/${encodeURIComponent(siteId)}/form-settings`,
  );
  return parseFormSettingsRow(data, siteId);
}

export async function updateFormSettings(siteId: string, input: UpdateFormSettingsInput): Promise<FormSettings> {
  const validated = updateFormSettingsInputSchema.parse(input);

  const data = await apiRequest<FormSettingsRow>(
    `/api/sites/${encodeURIComponent(siteId)}/form-settings`,
    { method: 'PUT', body: validated },
  );
  return parseFormSettingsRow(data, siteId);
}

export async function listFormSubmissions(siteId: string, limit = 20): Promise<FormSubmission[]> {
  const rows = await apiRequest<unknown[]>(
    `/api/sites/${encodeURIComponent(siteId)}/form-submissions?limit=${encodeURIComponent(String(limit))}`,
  );
  return rows.map((row) => formSubmissionSchema.parse(row));
}

export async function submitFormSubmission(siteId: string, payload: Record<string, unknown>) {
  await apiRequest(`/api/sites/${encodeURIComponent(siteId)}/form-submissions`, {
    method: 'POST',
    body: { payload },
  });
}

export async function submitPublicSiteForm(input: SubmitPublicSiteFormInput) {
  return apiRequest<{ success: true; message: string }>('/api/public/forms/submit', {
    method: 'POST',
    body: {
      siteId: input.siteId,
      pagePath: input.pagePath,
      payload: input.payload,
      honeypotValue: input.honeypotValue ?? '',
    },
  });
}

export async function deleteFormSubmission(siteId: string, submissionId: string) {
  await apiRequest(
    `/api/sites/${encodeURIComponent(siteId)}/form-submissions/${encodeURIComponent(submissionId)}`,
    { method: 'DELETE' },
  );
}

export async function purgeFormSubmissionsBefore(siteId: string, cutoffIso: string) {
  await apiRequest(`/api/sites/${encodeURIComponent(siteId)}/form-submissions`, {
    method: 'DELETE',
    body: { cutoffIso },
  });
}
